import { cn } from '@/lib/utils';
import { useGetComparatorPartnersQuery } from '@/service/propfirm-offer/propfirm-offer.hook';
import type { ComparatorPartner } from '@/service/propfirm-offer/propfirm-offer-types';
import { Tooltip } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MdContentCopy, MdOpenInNew } from 'react-icons/md';

/// Partenariats posés par un admin (lien, code, mise en avant), par slug.
export function usePartners(): Map<string, ComparatorPartner> {
  const { data } = useGetComparatorPartnersQuery();
  return useMemo(() => new Map((data ?? []).map((p) => [p.slug, p])), [data]);
}

export function PartnerBadge({ className }: { className?: string }) {
  const { t } = useTranslation('propfirms');
  return (
    <Tooltip title={t('partner.badgeTooltip')}>
      <span
        className={cn(
          'shrink-0 cursor-default rounded bg-primary-500/15 px-1.5 py-px text-[10px] font-medium text-primary-300',
          className,
        )}
      >
        {t('partner.badge')}
      </span>
    </Tooltip>
  );
}

/// Le code d'affiliation, copié d'un clic.
export function PartnerCode({ code }: { code: string }) {
  const { t } = useTranslation('propfirms');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      enqueueSnackbar(t('partner.codeCopied', { code }), {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar(t('partner.codeCopyFailed'), { variant: 'error' });
    }
  };
  return (
    <Tooltip title={t('partner.copyCode')}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          void copy();
        }}
        className="flex h-7 shrink-0 items-center gap-1.5 rounded border border-dashed border-primary-400/40 px-2 font-mono text-[11px] font-semibold tracking-wide text-primary-200 transition-colors hover:bg-primary-500/10 max-md:h-9"
      >
        {code}
        <MdContentCopy size={12} className="text-primary-300/70" />
      </button>
    </Tooltip>
  );
}

/// Le lien de l'offre : celui du partenaire s'il y en a un, le site sinon.
/// Le clic n'ouvre pas la fiche de la rangée.
export function OfferLink({
  website,
  partner,
  emphasis = false,
}: {
  website: string;
  partner?: ComparatorPartner;
  emphasis?: boolean;
}) {
  const { t } = useTranslation('propfirms');
  const url = partner?.affiliateUrl ?? website;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        window.api.openExternal(url);
      }}
      className={cn(
        'flex h-7 shrink-0 items-center gap-1 rounded px-2.5 text-[11px] font-medium transition-colors max-md:h-9',
        emphasis || partner?.affiliateUrl
          ? 'bg-primary-500 text-white hover:bg-primary-400'
          : 'text-neutral-400 hover:bg-white/[0.06] hover:text-neutral-200',
      )}
    >
      {partner?.affiliateUrl ? t('partner.offer') : t('partner.website')}
      <MdOpenInNew size={12} />
    </button>
  );
}
